import { useNavigate } from '@tanstack/react-router'
import { FilterFn, PaginationState, SortingState } from '@tanstack/react-table'
import { Button } from 'antd'
import { TableComponent } from 'components/table/TableComponent'
import { WebGLContext } from 'features/partsDatabase/contexts/WebGLLayer'
import { PartsData } from 'features/partsDatabase/types/partsTableTypes'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import PartsTableColumns from './PartsTableColumns'

interface PartsTableProps {
  siteID: string
  isLoading: boolean
  data: PartsData[]
}

const NoComponentsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`
export const PartsTable: React.FC<PartsTableProps> = (props) => {
  const { siteID, isLoading, data } = props
  const navigate = useNavigate()
  const { setAssetVizRenderingMap } = useContext(WebGLContext)

  const defaultColumns = PartsTableColumns(siteID)
  const columns = [...defaultColumns]
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'lastUpdated', desc: true },
  ])

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 24,
  })

  useEffect(() => {
    setAssetVizRenderingMap(new Map())
  }, [pagination, setAssetVizRenderingMap])

  const navigateToUnit = (unitID: string) => {
    navigate({
      to: '/assets/$siteID/$unitID',
      params: { siteID, unitID },
      search: { view: 'history' },
    })
  }
  const handleRowClick = (row: PartsData, event: React.MouseEvent) => {
    if (event.metaKey || event.ctrlKey) {
      const url = `/assets/${siteID}/${row.id}?view=history`
      window.open(url, '_blank')
      return
    }
    event.preventDefault()
    navigateToUnit(row.id)
  }

  const globalFilterFn: FilterFn<PartsData> = (row, _, filterValue) => {
    if (!row.original.componentName) {
      return false
    }
    return row.original.componentName
      ?.toLowerCase()
      .includes(
        typeof filterValue === 'string'
          ? filterValue.toLowerCase()
          : filterValue
      )
  }

  if (!data.some((item) => item.componentName)) {
    return (
      <NoComponentsContainer>
        <div>No components</div>
        <Button
          type='primary'
          onClick={() => {
            navigateToUnit(data[0].id)
          }}>
          <div>View Asset</div>
        </Button>
      </NoComponentsContainer>
    )
  }

  return (
    <TableComponent
      isLoadingData={isLoading}
      data={data}
      columns={columns}
      footerName={
        data.length === 0 ? 'No parts' : data.length === 1 ? 'Part' : 'Parts'
      }
      sorting={sorting}
      setSorting={setSorting}
      pagination={pagination}
      setPagination={setPagination}
      onRowClick={(row, event) => {
        handleRowClick(row.original, event)
      }}
      globalFilterFn={globalFilterFn}
    />
  )
}
